import React, { Suspense } from "react";
import { Outlet, useLocation } from "react-router-dom";
import { Container } from "@mui/material";
import makeStyles from "@mui/styles/makeStyles";
import { polyfillCSS } from "../../../utils/css";
import clsx from "clsx";
import { PageSkeleton } from "../../../components/loading/PageSkeleton";
import { COACH_PROGRAMS_FOLDERS_ROUTE } from "../../../routes/routes";
import {
  ComponentTemplatePrivacyType,
  ComponentTemplateSort,
  ComponentType,
} from "@growth-machine-llc/stridist-api-client";
import { TemplateListFilterContext } from "../../../contexts/TemplateLibraryFilterContext";
import { CoachComponentTemplateLibraryHeaderFilters } from "../../../components/component-templates-library/CoachComponentTemplateLibraryHeaderFilters";
import {
  AllOption,
  AllOptionType,
} from "../../../contexts/ProgramLibraryFilterContext";

const useStyles = makeStyles((theme) => ({
  root: {
    paddingTop: theme.spacing(2),
    paddingBottom: theme.spacing(11),
    paddingLeft: polyfillCSS(
      `calc(${theme.spacing(4)} + var(--safe-area-inset-left)) !important`,
    ),
    [theme.breakpoints.down("lg")]: {
      paddingLeft: "20px !important",
      paddingRight: "20px !important",
    },
  },
  filters: {
    marginBottom: theme.spacing(2),
  },
}));

export interface TemplateLibraryFilters {
  templatePrivacyType: ComponentTemplatePrivacyType;
  templateType: ComponentType[] | AllOptionType;
}

const CoachComponentTemplateLibraryWrapper = () => {
  const location = useLocation();
  const s = useStyles();

  const defaultFilters = {
    query: "",
    sortKey: ComponentTemplateSort.NAME_ASC,
  };

  const defaultTableFilters = {
    templatePrivacyType: ComponentTemplatePrivacyType.ALL,
    templateType: AllOption,
  };

  const [query, setQuery] = React.useState(defaultFilters.query);

  const [sortKey, setSortKey] = React.useState<ComponentTemplateSort>(
    defaultFilters.sortKey,
  );

  const [filters, setFilters] =
    React.useState<TemplateLibraryFilters>(defaultTableFilters);

  const { templatePrivacyType, templateType } = filters;

  const updateFilters = (newFilters: Partial<TemplateLibraryFilters>) => {
    setFilters((prev) => ({
      ...prev,
      ...newFilters,
    }));
  };

  const handleQueryChange = React.useCallback(
    (query: string) => {
      setQuery(query);
    },
    [setQuery],
  );

  const handleSortKeyChange = React.useCallback(
    (sortKey: ComponentTemplateSort) => {
      setSortKey(sortKey);
    },
    [setSortKey],
  );

  const handleTemplatePrivacyTypeChange = React.useCallback(
    (templatePrivacyType: ComponentTemplatePrivacyType) => {
      updateFilters({ templatePrivacyType });
    },
    [updateFilters],
  );

  const handleComponentTemplateTypeChange = React.useCallback(
    (type: ComponentType[] | AllOptionType) => {
      updateFilters({ templateType: type });
    },
    [updateFilters],
  );

  React.useEffect(() => {
    setQuery(defaultFilters.query);
    setSortKey(defaultFilters.sortKey);
  }, [location.pathname]);

  const handleResetFilters = React.useCallback(() => {
    setQuery(defaultFilters.query);
    setSortKey(defaultFilters.sortKey);
    updateFilters({
      templatePrivacyType: defaultTableFilters.templatePrivacyType,
    });
  }, [setQuery, setSortKey, updateFilters]);

  const filterContextValue = {
    query,
    sortKey,
    templatePrivacyType,
    templateType,
    handleQueryChange,
    handleSortKeyChange,
    handleTemplatePrivacyTypeChange,
    handleResetFilters,
    handleComponentTemplateTypeChange,
  };

  const filtersToReset =
    templatePrivacyType !== defaultTableFilters.templatePrivacyType ||
    templateType !== defaultTableFilters.templateType ||
    query !== defaultFilters.query ||
    sortKey !== defaultFilters.sortKey;

  return (
    <TemplateListFilterContext.Provider value={filterContextValue}>
      <Container className={clsx(s.root)} maxWidth="llg">
        {location.pathname !== COACH_PROGRAMS_FOLDERS_ROUTE && (
          <CoachComponentTemplateLibraryHeaderFilters
            className={s.filters}
            query={query}
            onChangeQuery={handleQueryChange}
            templatePrivacyType={templatePrivacyType}
            onChangeTemplatePrivacyType={handleTemplatePrivacyTypeChange}
            sortKey={sortKey}
            onChangeSortKey={handleSortKeyChange}
            defaultValues={{ ...defaultTableFilters, ...defaultFilters }}
            showResetButton={true}
            onResetFilters={handleResetFilters}
            disabledResetButton={!filtersToReset}
          />
        )}
        <Suspense fallback={<PageSkeleton fullWidth />}>
          <Outlet />
        </Suspense>
      </Container>
    </TemplateListFilterContext.Provider>
  );
};

export default CoachComponentTemplateLibraryWrapper;
